import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch, FaChevronRight } from 'react-icons/fa';
import { getApplicationsList } from '../services/subscriberWalletService';
import { useAuth } from '../auth/AuthContext';

const ManualTransaction = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // State for API data
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search state - initialize from URL or empty
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);
  
  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();
  
  const PAGE_SIZE = 25;
  // Initialize page from URL or default to 1
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [pagination, setPagination] = useState({
    page: 1,
    items: PAGE_SIZE,
    total: 0,
    total_pages: 0
  });
  
  const isFinanceOrSupport = currentRole === 'Finance' || currentRole === 'Support';
  
  // Check if user has access (Ops, Finance, and SuperAdmin)
  const canAccess = currentRole === 'Ops' || currentRole === 'Finance' || currentRole === 'SuperAdmin';
  
  // Track last URL update to prevent unnecessary updates
  const lastUrlUpdateRef = useRef((() => {
    const initialPage = searchParams.get('page');
    const initialSearch = searchParams.get('search');
    return {
      page: initialPage || null,
      search: initialSearch || null
    };
  })());
  
  // Track if initial mount is complete to prevent duplicate calls
  const isInitialMountRef = useRef(true);
  // Track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef({ page: null, search: null, role: null });
  // Track if API call is in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);
  
  // Update URL when page or search changes (but don't trigger API call)
  useEffect(() => {
    const newPageStr = page > 1 ? page.toString() : null;
    const newSearchStr = search.trim() || null;
    
    // Only update if values actually changed
    if (lastUrlUpdateRef.current.page !== newPageStr || lastUrlUpdateRef.current.search !== newSearchStr) {
      const params = new URLSearchParams();
      if (newPageStr) {
        params.set('page', newPageStr);
      }
      if (newSearchStr) {
        params.set('search', newSearchStr);
      }
      setSearchParams(params, { replace: true });
      lastUrlUpdateRef.current = { page: newPageStr, search: newSearchStr };
    }
  }, [page, search, setSearchParams]);

  // Handle search submission (Enter key or debounce)
  const handleSearchSubmit = useCallback(() => {
    const newSearch = searchInput.trim();
    if (newSearch !== search) {
      setSearch(newSearch);
      setPage(1); // Reset to page 1 when search changes
    }
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [searchInput, search]);

  // Debounce search input (2 seconds)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchSubmit();
    }, 2000);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, handleSearchSubmit]);
  
  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    const last4 = digits.slice(-4);
    return `******${last4}`;
  };
  
  // Load applications from API with server-side pagination and search
  useEffect(() => {
    // Skip on initial mount if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Skip if not authenticated or no role
    if (!isAuthenticated || !currentRole) {
      return;
    }
    
    // Use page from state (which is initialized from URL on mount)
    const currentPage = page || 1;
    const currentSearch = search.trim() || '';
    
    // Only load data if there's a search query
    if (!currentSearch) {
      setApplications([]);
      setPagination({
        page: 1,
        items: PAGE_SIZE,
        total: 0,
        total_pages: 0
      });
      setLoading(false);
      return;
    }
    
    // Prevent duplicate API calls with same parameters
    if (lastApiCallRef.current.page === currentPage && 
        lastApiCallRef.current.search === currentSearch &&
        lastApiCallRef.current.role === currentRole) {
      return;
    }
    
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }
    
    // Update last API call reference
    lastApiCallRef.current = { page: currentPage, search: currentSearch, role: currentRole };
    
    const loadApplications = async () => {
      // Mark as loading
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Build query parameters for pagination
        const params = {
          page: currentPage,
          items: PAGE_SIZE
        };
        
        // Add search parameter if search query exists
        if (currentSearch) {
          params.search = currentSearch;
        }
        
        const response = await getApplicationsList(currentRole, params);
        
        let applicationsData = null;
        let paginationData = null;
        
        // Handle response structure: { msg: { items: [...], pagination: {...} }, metadata: {...} }
        if (response && response.msg) {
          if (response.msg.items && Array.isArray(response.msg.items)) {
            applicationsData = response.msg.items;
          }
          if (response.msg.pagination) {
            paginationData = response.msg.pagination;
          }
        } else if (response && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            applicationsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (response && response.success && response.data) {
          if (response.data.items && Array.isArray(response.data.items)) {
            applicationsData = response.data.items;
          }
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        } else if (Array.isArray(response)) {
          // Fallback: if response is directly an array
          applicationsData = response;
          paginationData = {
            page: 1,
            items: response.length,
            total: response.length,
            total_pages: Math.ceil(response.length / PAGE_SIZE)
          };
        }
        
        if (applicationsData && Array.isArray(applicationsData)) {
          setApplications(applicationsData);
          if (paginationData) {
            // Calculate total_pages from total and items (backend doesn't provide total_pages)
            const calculatedTotalPages = Math.ceil(paginationData.total / paginationData.items);
            setPagination({
              ...paginationData,
              total_pages: calculatedTotalPages
            });
          } else {
            // Default pagination if not provided (shouldn't happen if backend is correct)
            setPagination({
              page: page || 1,
              items: PAGE_SIZE,
              total: applicationsData.length,
              total_pages: Math.ceil(applicationsData.length / PAGE_SIZE)
            });
          }
        } else {
          setApplications([]);
          setPagination({
            page: 1,
            items: PAGE_SIZE,
            total: 0,
            total_pages: 0
          });
          setError(`Failed to load applications - Response: ${JSON.stringify(response).substring(0, 200)}...`);
        }
      } catch (error) {
        console.error('Error loading applications:', error);
        setError('Failed to load applications');
        setApplications([]);
        setPagination({
          page: 1,
          items: PAGE_SIZE,
          total: 0,
          total_pages: 0
        });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        // Mark initial mount as complete after first successful load
        if (isInitialMountRef.current) {
          isInitialMountRef.current = false;
        }
      }
    };

    loadApplications();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search]);
  
  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Check access for manual transaction (Ops, Finance, and SuperAdmin)
  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Manual Transaction. This feature is only available for Ops, Finance, and SuperAdmin roles.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="mr-2">Manual Transaction</span>
              </h2>
            </div>
            <div className="mt-3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, or REF CODE..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {!search.trim() && !loading && (
            <div className="px-6 py-8 text-center text-gray-500">
              Enter a search query to find subscribers by Name, Mobile, or REF CODE.
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="px-6 py-4 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && search.trim() && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REF CODE</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet Balance</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No subscribers found
                      </td>
                    </tr>
                  ) : (
                    applications.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/subscriber-wallet/manual-transaction/subscriber/${encodeURIComponent(r.id)}`)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {r.personal?.full_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {isFinanceOrSupport
                            ? (r.mobile_display || maskMobile(r.personal?.mobile || r.mobile))
                            : (r.personal?.mobile || r.mobile || r.mobile_display || 'N/A')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {r.ref_code || r.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {r.plan || r.personal_subscription || r.subscription_plan || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            (r.status === 'Active' || r.status === 'active' || r.active) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {r.status || (r.active ? 'Active' : 'Inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{(r.wallet_balance || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400">
                          <FaChevronRight />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && search.trim() && pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.items) + 1}-{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                  const pageNum = i + 1;
                  // Show first page, last page, current page, and pages around current
                  const totalPages = pagination.total_pages || 1;
                  const shouldShow = 
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                  
                  if (!shouldShow) {
                    // Show ellipsis
                    if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                      return (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualTransaction;

